import mixpanel from 'mixpanel-browser';
import PptxGenJS from 'pptxgenjs';

import { getAuthStoreUser, getEffectiveOverrides } from '@revelio/auth';
import { FeatureTrackingEvents } from '@revelio/core';
import { ReplotChartNames } from '@revelio/replots';

import { getBrandConfig } from '../../../../../../../shared/branding/brand-config';
import type { Position } from '../../../../PageConfigProvider/types/item';
import type { ChartExportInfo } from '../../chart-export-types';
import { MIXED_STATS_DATA_TYPE } from '../registry';
import {
  buildSheetData,
  generateChartData,
  processMixedStatsData,
} from './ppt-data-processor';
import { getReplotChartType, mapReplotToPptChart } from './ppt-exporter';
import {
  addReportChartToSlide,
  addReportDivergingBarComboToSlide,
  addReportFooterSlide,
  addReportGaugeToSlide,
  addReportTableToSlide,
  addReportTextToSlide,
} from './ppt-slide-generator';
import {
  defineReportSlideMasters,
  PptxChartType,
  REPORT_LAYOUT_NAME,
  reportPptTheme,
} from './ppt-theme';

export type ReportChartExportParams = ChartExportInfo;

export type ReportExportParams = {
  charts: ReportChartExportParams[];
  reportTitle: string;
  reportDescription?: string;
  coverParameters?: { label: string; value: string }[];
  gridColumns: number;
  gridRows: number;
  pageGridRows: number;
};

type PptExportResult = {
  getFile: () => Promise<void>;
  getBlob: () => Promise<{ blob: Blob; fileName: string } | null>;
};

const addReportCoverSlide = (
  pptx: PptxGenJS,
  params: Pick<
    ReportExportParams,
    'reportTitle' | 'reportDescription' | 'coverParameters'
  >
) => {
  const { widthInches, heightInches, marginInches } = reportPptTheme.slide;
  const cover = reportPptTheme.cover;

  const user = getAuthStoreUser();
  const overrides = getEffectiveOverrides();
  const clientName = overrides?.clientName ?? user?.client_name;
  const brand = getBrandConfig(clientName);
  const titleColor = brand.coverTitleColor.replace('#', '');
  const descriptionColor = brand.coverDescriptionColor.replace('#', '');

  const slide = pptx.addSlide({ masterName: 'REPORT_COVER_SLIDE' });

  const LOGO_ASPECT_RATIO = 185 / 27;
  const logoW = 1.5;
  const logoH = logoW / LOGO_ASPECT_RATIO;

  slide.addImage({
    path: brand.logos.reportLogo,
    x: marginInches,
    y: marginInches,
    w: logoW,
    h: logoH,
  });

  const contentX = marginInches;
  const contentW = widthInches - 2 * marginInches;
  const titleY = heightInches * cover.titleTopPercent;

  slide.addText(params.reportTitle || 'Title', {
    x: contentX,
    y: titleY,
    w: contentW,
    h: 1.0,
    fontSize: cover.titleFontSize,
    bold: true,
    color: titleColor,
    fontFace: 'Arial',
    align: 'left',
    valign: 'top',
  });

  const descriptionY = titleY + 1.1;
  const descriptionBoxH = params.reportDescription?.trim()
    ? cover.descriptionMaxHeightInches
    : 0;

  if (params.reportDescription?.trim()) {
    slide.addText(params.reportDescription, {
      x: contentX,
      y: descriptionY,
      w: contentW,
      h: descriptionBoxH,
      fontSize: cover.descriptionFontSize,
      color: descriptionColor,
      fontFace: 'Arial',
      align: 'left',
      valign: 'top',
    });
  }

  const parameters = params.coverParameters ?? [];
  if (parameters.length > 0) {
    const parametersY =
      descriptionY + descriptionBoxH + cover.gapDescriptionToParametersInches;
    const mid = Math.ceil(parameters.length / 2);
    const leftColumn = parameters.slice(0, mid);
    const rightColumn = parameters.slice(mid);
    const columnW = (contentW - 0.3) / 2;

    const buildRuns = (entries: { label: string; value: string }[]) => {
      const runs: PptxGenJS.TextProps[] = [];
      entries.forEach((param, index) => {
        runs.push({
          text: param.label,
          options: {
            bold: true,
            color: titleColor,
            fontSize: cover.parameterLabelFontSize,
            breakLine: true,
          },
        });
        runs.push({
          text: param.value,
          options: {
            color: descriptionColor,
            fontSize: cover.parameterValueFontSize,
            breakLine: index < entries.length - 1,
          },
        });
        if (index < entries.length - 1) {
          runs.push({
            text: '',
            options: { fontSize: 4, breakLine: true },
          });
        }
      });
      return runs;
    };

    slide.addText(buildRuns(leftColumn), {
      x: contentX,
      y: parametersY,
      w: columnW,
      h: 2.0,
      fontFace: 'Arial',
      valign: 'top',
    });

    if (rightColumn.length > 0) {
      slide.addText(buildRuns(rightColumn), {
        x: contentX + columnW + 0.3,
        y: parametersY,
        w: columnW,
        h: 2.0,
        fontFace: 'Arial',
        valign: 'top',
      });
    }
  }
};

type LaidOutChart = {
  chart: ReportChartExportParams;
  layout: { x: number; y: number; w: number; h: number };
};

const computeChartLayout = ({
  position,
  pageStartRow,
  pageGridRows,
  gridColumns,
}: {
  position: Position;
  pageStartRow: number;
  pageGridRows: number;
  gridColumns: number;
}) => {
  const { widthInches, heightInches, marginInches } = reportPptTheme.slide;
  const contentW = widthInches - 2 * marginInches;
  const relativeY = position.y - pageStartRow;
  const x = marginInches + (position.x / gridColumns) * contentW;
  const y = (relativeY / pageGridRows) * heightInches;
  const w = (position.width / gridColumns) * contentW;
  const h = (position.height / pageGridRows) * heightInches;
  return { x, y, w, h };
};

const addChartToReportSlide = async (
  slide: PptxGenJS.Slide,
  chart: ReportChartExportParams,
  layout: { x: number; y: number; w: number; h: number }
): Promise<void> => {
  const { title, selectedSegments, data, dataType, textContent } = chart;

  if (dataType === 'text') {
    const body = textContent?.trim();
    if (!body) return;
    await addReportTextToSlide(slide, body, { ...layout, title });
    return;
  }

  if (chart.plotChartType === 'gauge') {
    addReportGaugeToSlide(slide, {
      ...layout,
      mainText: chart.gaugeMainText,
      subText: chart.gaugeSubText,
      unit: chart.gaugeUnit,
    });
    return;
  }

  const isDivergingBarComboExport =
    chart.plotChartType === 'diverging-bar-combo' ||
    chart.chartType === ReplotChartNames.DivergingBarCombo;

  if (!data) {
    console.warn(`No data to export for slide: ${title}`);
    return;
  }

  if (isDivergingBarComboExport) {
    addReportDivergingBarComboToSlide(slide, data, {
      ...layout,
      title,
      dataType,
      selectedSegments,
    });
    return;
  }

  const replotChartType = getReplotChartType({
    title,
    chartType: chart.chartType,
    plotChartType: chart.plotChartType,
    dataType,
  });

  const { sheetData } = buildSheetData(data, dataType, replotChartType);
  if (!sheetData || sheetData.length === 0) {
    console.warn(`No data to export for slide: ${title}`);
    return;
  }

  if (replotChartType === ReplotChartNames.TableChart) {
    addReportTableToSlide(
      slide,
      sheetData as Record<string, string | number>[],
      {
        ...layout,
        columnMetaMap: chart.tableColumnMetaMap,
        tablePlotFormat: chart.tablePlotFormat,
        currencyCode: chart.tableCurrencyCode,
      }
    );
    return;
  }

  if (dataType === MIXED_STATS_DATA_TYPE) {
    const miniCharts = processMixedStatsData(sheetData) || [];
    const first = miniCharts[0];
    if (!first) return;
    const chartData = [
      {
        name: first.name,
        labels: first.labels.map((l) => String(l)),
        values: first.values,
      },
    ];
    const mixedTitle = title.trim();
    const showMixedTitle = mixedTitle.length > 0;
    slide.addChart(PptxChartType.area, chartData, {
      ...layout,
      title: showMixedTitle ? mixedTitle : '',
      showTitle: showMixedTitle,
      showLegend: false,
      chartColors: ['5BD992'],
      chartColorsOpacity: 60,
    });
    return;
  }

  const pptChart = mapReplotToPptChart(replotChartType);
  if (!pptChart) return;
  const chartData = generateChartData(sheetData, replotChartType);

  addReportChartToSlide(
    slide,
    chartData,
    pptChart as unknown as {
      type: PptxGenJS.CHART_NAME;
      opts?: Record<string, unknown>;
    },
    {
      ...layout,
      title,
      chartType: replotChartType ?? undefined,
      selectedSegments,
    }
  );
};

const groupChartsByPage = ({
  charts,
  pageGridRows,
  gridRows,
  gridColumns,
}: {
  charts: ReportChartExportParams[];
  pageGridRows: number;
  gridRows: number;
  gridColumns: number;
}): Map<number, LaidOutChart[]> => {
  const totalPages = Math.max(1, Math.ceil(gridRows / pageGridRows));
  const lastPageIndex = totalPages - 1;
  const pageMap = new Map<number, LaidOutChart[]>();

  for (const chart of charts) {
    if (!chart.position) continue;
    const pageIndex = Math.floor(chart.position.y / pageGridRows);
    if (pageIndex <= 0) continue;
    if (pageIndex >= lastPageIndex && totalPages > 2) continue;

    const bucket = pageMap.get(pageIndex) ?? [];
    bucket.push({
      chart,
      layout: computeChartLayout({
        position: chart.position,
        pageStartRow: pageIndex * pageGridRows,
        pageGridRows,
        gridColumns,
      }),
    });
    pageMap.set(pageIndex, bucket);
  }

  return pageMap;
};

const createReportPptxDoc = async (
  params: ReportExportParams
): Promise<{ pptx: PptxGenJS; fileName: string }> => {
  const pptx = new PptxGenJS();

  const { widthInches, heightInches } = reportPptTheme.slide;
  pptx.defineLayout({
    name: REPORT_LAYOUT_NAME,
    width: widthInches,
    height: heightInches,
  });
  pptx.layout = REPORT_LAYOUT_NAME;

  defineReportSlideMasters(pptx);

  addReportCoverSlide(pptx, {
    reportTitle: params.reportTitle,
    reportDescription: params.reportDescription,
    coverParameters: params.coverParameters,
  });

  const pagedCharts = groupChartsByPage({
    charts: params.charts,
    pageGridRows: params.pageGridRows,
    gridRows: params.gridRows,
    gridColumns: params.gridColumns,
  });

  const sortedPageIndices = Array.from(pagedCharts.keys()).sort(
    (a, b) => a - b
  );

  for (const pageIndex of sortedPageIndices) {
    const slide = pptx.addSlide();
    const chartsForPage = pagedCharts.get(pageIndex) ?? [];
    for (const entry of chartsForPage) {
      await addChartToReportSlide(slide, entry.chart, entry.layout);
    }
  }

  addReportFooterSlide(pptx);

  const fileName = `${params.reportTitle || 'report'}.pptx`;
  return { pptx, fileName };
};

export const exportReportToPpt = (
  params: ReportExportParams
): PptExportResult => ({
  async getFile() {
    const { fileName, pptx } = await createReportPptxDoc(params);

    mixpanel.track(FeatureTrackingEvents.PPT_DOWNLOAD, {
      plot_title: params.reportTitle,
    });

    await pptx.writeFile({ fileName });
  },
  async getBlob() {
    const { fileName, pptx } = await createReportPptxDoc(params);
    const blob = (await pptx.write({ outputType: 'blob' })) as Blob;

    mixpanel.track(FeatureTrackingEvents.PPT_PREVIEW, {
      plot_title: params.reportTitle,
    });

    return { blob, fileName };
  },
});
